import { AIR, dcos, dsin } from '@vm/shared';
import { M } from '../palette';
import { MapBuilder, speckle, stripes, symPoint, symYaw, yawToward, type Brush } from './builder';
import { awning, bed, bin, bus, counter, G, house as kitHouse, lampPost, mailbox, partition, PETAL, planter, shelf, sofa, table, TRIM, type Box, type HouseSpec } from './kit';
import { emptyMeta, type MapDef, type MapMeta } from './types';

/**
 * Pastel Court: a toy-bright suburban cul-de-sac in the layout of a famous small two-house arena map (see docs/maps/reference-maps.md):
 * a house and its back yard at each end, joined by one open street with a roundabout and parked vehicles in the middle, inside a
 * boundary that bends in a bat-wing shape so the corners are narrow and the middle wide. The plan is the reference's; everything you
 * see is ours: pastel clapboard houses with furnished rooms, picket fences, hedges, a play yard, a greenhouse and a water tower.
 *
 * The west half is built and mirrored; the roundabout and the water tower on the centre line are built after the mirror.
 * Play-space coordinates are metres from the north-west corner of the 96 x 41.2 m play area; `px` / `pz` turn them into voxels.
 */
const SX = 1024;
const SY = 160;
const SZ = 544;
const OX = 32; // play area origin (voxels)
const OZ = 66;
const PW = 96; // play area width, metres
const PD = 41.2; // play area depth, metres
const SYM = 'mirrorX' as const;
const px = (x: number): number => OX + Math.round(x * 10);
const pz = (z: number): number => OZ + Math.round(z * 10);
const wx = (x: number): number => OX / 10 + x; // play metres -> world metres
const wz = (z: number): number => OZ / 10 + z;
const FENCE_H = 58; // height of the boundary hedge (voxels)

/** West half of the boundary, from the top of the centre line round to the bottom of it; the east half is the mirror image. */
const WEST_OUTLINE: [number, number][] = [
  [48, 2.0], [40, 4.4], [33.3, 8.1], [10.8, 0.0], [5.3, 8.4], [0, 23.1], [33.1, 34.1], [41.6, 34.0], [41.6, PD], [48, PD],
];
const OUTLINE: [number, number][] = [...WEST_OUTLINE, ...[...WEST_OUTLINE].reverse().slice(1, -1).map(([x, z]): [number, number] => [PW - x, z])];

/** Point-in-polygon (even-odd), play metres. */
function inside(x: number, z: number): boolean {
  let c = false;
  for (let i = 0, j = OUTLINE.length - 1; i < OUTLINE.length; j = i++) {
    const [xi, zi] = OUTLINE[i];
    const [xj, zj] = OUTLINE[j];
    if (zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) c = !c;
  }
  return c;
}

/** Distance (metres) from a point to the boundary. */
function edgeDistance(x: number, z: number): number {
  let best = 1e9;
  for (let i = 0, j = OUTLINE.length - 1; i < OUTLINE.length; j = i++) {
    const [ax, az] = OUTLINE[j];
    const [bx, bz] = OUTLINE[i];
    const dx = bx - ax;
    const dz = bz - az;
    const t = Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / (dx * dx + dz * dz)));
    const ex = ax + dx * t - x;
    const ez = az + dz * t - z;
    best = Math.min(best, ex * ex + ez * ez);
  }
  return Math.sqrt(best);
}

/** Runs `fn` only when a prop centred at (x, z) metres with radius `r` lies fully inside the boundary (keeps props out of the hedge). */
function place(x: number, z: number, r: number, fn: () => void): void {
  if (inside(x, z) && edgeDistance(x, z) > r) fn();
}

/** House with its box given in play-space metres. */
function house(b: MapBuilder, s: Omit<HouseSpec, 'x0' | 'z0' | 'x1' | 'z1'> & { x0: number; z0: number; x1: number; z1: number }): Box {
  return kitHouse(b, { ...s, x0: px(s.x0), x1: px(s.x1), z0: pz(s.z0), z1: pz(s.z1) });
}

// Palette ---------------------------------------------------------------------------------------
const SIDING = ['siding_peach', 'siding_mint', 'siding_lilac', 'siding_sky', 'siding_butter', 'siding_rose'].map((n) => M(n));
const LAWN: Brush = speckle([M('grass_1'), M('grass_3'), M('grass_1'), M('grass_2')], 31);
const HEDGE: Brush = speckle([M('leaves_1'), M('leaves_2'), M('leaves_3')], 33);

function hedge(b: MapBuilder, x0: number, z0: number, x1: number, z1: number, h = 12, t = 6): void {
  // Axis-aligned run (metres) of clipped hedge on a low stone kerb.
  const X0 = px(Math.min(x0, x1));
  const X1 = px(Math.max(x0, x1));
  const Z0 = pz(Math.min(z0, z1));
  const Z1 = pz(Math.max(z0, z1));
  const alongX = X1 - X0 >= Z1 - Z0;
  const [xa, xb, za, zb] = alongX ? [X0, X1, Z0 - (t >> 1), Z0 + (t >> 1)] : [X0 - (t >> 1), X0 + (t >> 1), Z0, Z1];
  b.box(xa, G, za, xb, G + 2, zb, M('stone_1'));
  b.box(xa, G + 2, za, xb, G + h, zb, HEDGE);
  b.box(xa + 1, G + h, za + 1, xb - 1, G + h + 1, zb - 1, HEDGE);
}

function picket(b: MapBuilder, x0: number, z0: number, x1: number, z1: number, h = 10): void {
  // White picket fence: two rails and a board every second voxel, so you can see and shoot through between the boards.
  const X0 = px(Math.min(x0, x1));
  const X1 = px(Math.max(x0, x1));
  const Z0 = pz(Math.min(z0, z1));
  const Z1 = pz(Math.max(z0, z1));
  if (X1 - X0 >= Z1 - Z0) {
    b.box(X0, G + 2, Z0, X1, G + 3, Z0 + 1, TRIM);
    b.box(X0, G + h - 3, Z0, X1, G + h - 2, Z0 + 1, TRIM);
    for (let x = X0; x < X1; x += 2) {
      b.box(x, G, Z0, x + 1, G + h, Z0 + 1, TRIM);
      b.box(x, G + h, Z0, x + 1, G + h + 1, Z0 + 1, M('paint_pink'));
    }
  } else {
    b.box(X0, G + 2, Z0, X0 + 1, G + 3, Z1, TRIM);
    b.box(X0, G + h - 3, Z0, X0 + 1, G + h - 2, Z1, TRIM);
    for (let z = Z0; z < Z1; z += 2) {
      b.box(X0, G, z, X0 + 1, G + h, z + 1, TRIM);
      b.box(X0, G + h, z, X0 + 1, G + h + 1, z + 1, M('paint_pink'));
    }
  }
}

function build(b: MapBuilder): MapMeta {
  // Ground and the boundary hedge ------------------------------------------------------------------------------
  b.ground(G, LAWN, speckle([M('dirt_1'), M('dirt_3')], 32), speckle([M('rock_1'), M('rock_2')], 34));
  // Boundary: a band 4 m thick all round the play area. Its core is tough rock (leaves alone are one-hit blocks, so players could shoot
  // their way out); the faces you see are hedge on a white picket skirt, and the top is hedge too.
  for (let X = OX - 45; X < SX / 2 + 1; X++) {
    const xm = (X - OX + 0.5) / 10;
    for (let Z = OZ - 45; Z < OZ + Math.round(PD * 10) + 45; Z++) {
      const zm = (Z - OZ + 0.5) / 10;
      if (inside(xm, zm)) continue;
      const d = edgeDistance(xm, zm);
      if (d > 4.0) continue;
      if (d <= 0.45) {
        b.box(X, G, Z, X + 1, G + 12, Z + 1, TRIM);
        b.box(X, G + 12, Z, X + 1, G + FENCE_H, Z + 1, HEDGE);
      } else if (d >= 3.2) {
        b.box(X, G, Z, X + 1, G + FENCE_H, Z + 1, HEDGE); // the outer face is hedge too, so the fence has no grey cliff from the air
      } else {
        b.box(X, G, Z, X + 1, G + FENCE_H - 2, Z + 1, M('rock_1'));
        b.box(X, G + FENCE_H - 2, Z, X + 1, G + FENCE_H, Z + 1, HEDGE);
      }
    }
  }

  // Surfaces: the street (with a sidewalk) in the middle, lawn everywhere else ----------------------------------------
  for (let X = OX; X < SX / 2; X++) {
    const xm = (X - OX + 0.5) / 10;
    if (xm < 38.5) continue;
    for (let Z = OZ; Z < OZ + Math.round(PD * 10); Z++) {
      const zm = (Z - OZ + 0.5) / 10;
      if (!inside(xm, zm)) continue;
      if (xm <= 40.0) b.set(X, G - 1, Z, (X >> 3) % 2 ? M('concrete_3') : M('concrete_1'));
      else if (xm <= 40.6) b.set(X, G - 1, Z, M('stone_1'));
      else b.set(X, G - 1, Z, (X + Z) % 5 === 0 ? M('asphalt_2') : M('asphalt_1'));
    }
  }
  // Yellow dashes down the centre of the street (the mirror completes the other side of the line).
  for (let Z = OZ; Z < OZ + Math.round(PD * 10); Z += 14) for (let X = SX / 2 - 3; X < SX / 2; X++) for (let k = 0; k < 7; k++) if (inside(48, (Z + k - OZ) / 10)) b.set(X, G - 1, Z + k, M('road_yellow'));

  // The main house ----------------------------------------------------------------------------------------------------------
  const h1 = house(b, {
    x0: 24.5, z0: 10.6, x1: 38.4, z1: 22.0, floors: 2, wall: SIDING[0], roof: M('roof_red_1'),
    doors: [{ side: 'e', w: 16, h: 26 }, { side: 'w', w: 16, h: 26 }, { side: 's', at: 8, w: 16, h: 26 }, { side: 'n', at: 90, w: 16, h: 26 }],
  });
  const g1 = house(b, {
    x0: 27.0, z0: 21.8, x1: 34.4, z1: 27.6, floors: 1, wall: SIDING[1], roof: M('roof_slate'), roofStyle: 'flat',
    doors: [{ side: 'e', w: 30, h: 26 }, { side: 'w', w: 16, h: 26 }],
  });
  // Interior (voxels): west hall with the stairs, living room (north-east), kitchen (south-east), a door through to the garage.
  const hx = h1.x0;
  const hz = h1.z0;
  const mid = hx + 60;
  partition(b, 0, 'z', mid, hz + 2, h1.z1 - 2, hz + 49); // hall | rooms, doorway lines up with the front and back doors
  partition(b, 0, 'x', hz + 70, mid + 2, h1.x1 - 2, mid + 38); // living room | kitchen
  b.clear(hx + 62, G, h1.z1 - 2, hx + 78, G + 26, h1.z1 + 0); // kitchen -> garage door
  sofa(b, mid + 4, G, hz + 4, M('cloth_blue'));
  table(b, mid + 44, G, hz + 34, 22, 14);
  shelf(b, h1.x1 - 6, G, hz + 4, 4, 36, 22);
  counter(b, mid + 20, G, h1.z1 - 9, 56, 7);
  counter(b, h1.x1 - 9, G, hz + 74, 7, 26);
  table(b, mid + 6, G, hz + 82, 20, 12);
  shelf(b, hx + 3, G, hz + 80, 4, 26, 22);
  // Upstairs: a landing, then two bedrooms.
  partition(b, 1, 'z', mid, hz + 2, h1.z1 - 2, hz + 2);
  bed(b, hx + 3, G + 30, hz + 64, false, M('cloth_red'));
  shelf(b, mid - 6, G + 30, hz + 40, 4, 40, 20);
  bed(b, mid + 40, G + 30, hz + 80, true, M('cloth_blue'));
  table(b, mid + 6, G + 30, hz + 86, 18, 10);
  shelf(b, h1.x1 - 6, G + 30, hz + 6, 4, 30, 20);
  // Garage: work bench, shelves and boxes; a clear lane through the middle.
  shelf(b, g1.x0 + 3, G, g1.z0 + 12, 3, 36, 18);
  counter(b, g1.x1 - 10, G, g1.z0 + 12, 7, 38);
  b.crate(g1.x0 + 8, G, g1.z1 - 14, 8);
  // Chimney on the west gable and window boxes under the front windows.
  b.box(h1.x0 - 8, G, hz + 80, h1.x0 + 1, G + 84, hz + 92, M('brick_red_3'));
  b.box(h1.x0 - 9, G + 84, hz + 79, h1.x0 + 2, G + 86, hz + 93, M('stone_1'));
  for (const zz of [hz + 14, hz + 88]) planter(b, h1.x1, zz, 4, 14, [M('paint_pink'), M('paint_yellow'), M('paint_magenta')], G + 12);
  awning(b, 'e', h1, (h1.z0 + h1.z1) / 2 - 8, 16, M('paint_pink'));
  awning(b, 'w', h1, (h1.z0 + h1.z1) / 2 - 8, 16, M('paint_cyan'));

  // Back yard (west): a shed, a greenhouse, a play frame, a sandbox, fenced beds and a tree --------------------------------------------
  place(16.0, 6.4, 1.2, () => {
    const shed = house(b, { x0: 13.5, z0: 4.6, x1: 18.5, z1: 8.4, floors: 1, wall: SIDING[2], roof: M('roof_blue'), roofStyle: 'gableX', doors: [{ side: 's', w: 16, h: 26 }, { side: 'e', w: 16, h: 26 }] });
    shelf(b, shed.x0 + 3, G, shed.z0 + 3, 20, 4, 18);
    b.crate(shed.x0 + 30, G, shed.z0 + 18, 8);
  });
  {
    // Greenhouse: glass walls on a brick plinth with a doorway on its west side.
    const x0 = px(12.0);
    const x1 = px(17.6);
    const z0 = pz(21.9);
    const z1 = pz(26.1);
    b.box(x0, G, z0, x1, G + 4, z1, M('brick_red_3'));
    b.clear(x0 + 2, G, z0 + 2, x1 - 2, G + 4, z1 - 2);
    b.walls(x0, G + 4, z0, x1, G + 24, z1, 1, M('glass'));
    for (const [cx, cz] of [[x0, z0], [x1 - 1, z0], [x0, z1 - 1], [x1 - 1, z1 - 1]]) b.box(cx, G + 4, cz, cx + 1, G + 25, cz + 1, TRIM);
    b.box(x0, G + 24, z0, x1, G + 25, z1, TRIM);
    b.box(x0 + 1, G + 25, z0 + 1, x1 - 1, G + 26, z1 - 1, M('glass'));
    b.clear(x0, G, z0 + 12, x0 + 2, G + 24, z0 + 28);
    b.box(x0 - 4, G - 1, z0 + 12, x0 + 2, G, z0 + 28, M('concrete_3'));
    planter(b, x0 + 8, z0 + 5, 14, 5, [M('paint_yellow'), M('paint_orange'), M('paint_red')], G + 4);
  }
  place(8.0, 11.0, 2.0, () => {
    // Swing set (two A-frames, a top bar and two swings) and a sandbox.
    const x = px(5.8);
    const z = pz(10.4);
    for (const dx of [0, 36]) {
      b.line(x + dx, G, z, x + dx + 4, G + 22, z + 3, M('paint_red'), 2);
      b.line(x + dx + 8, G, z, x + dx + 4, G + 22, z + 3, M('paint_red'), 2);
    }
    b.box(x + 2, G + 22, z + 2, x + 40, G + 24, z + 4, M('paint_yellow'));
    for (const s of [10, 28]) {
      b.box(x + s, G + 8, z + 3, x + s + 1, G + 22, z + 4, M('steel_dark_1'));
      b.box(x + s + 6, G + 8, z + 3, x + s + 7, G + 22, z + 4, M('steel_dark_1'));
      b.box(x + s, G + 7, z + 2, x + s + 7, G + 8, z + 5, M('paint_blue'));
    }
  });
  place(15.0, 12.0, 1.5, () => {
    planter(b, px(14.4), pz(10.8), 22, 14, [M('sand_1')]);
    b.box(px(14.4) + 1, G + 4, pz(10.8) + 1, px(14.4) + 21, G + 5, pz(10.8) + 13, M('sand_3'));
  });
  // Screens: hedges and pickets wall the yard off from the street, with gaps to walk through.
  hedge(b, 12.5, 14.4, 21.5, 14.4, 11, 5);
  hedge(b, 12.5, 21.0, 20.6, 21.0, 11, 5);
  picket(b, 22.4, 12.5, 22.4, 19.8, 10);
  picket(b, 22.4, 23.0, 22.4, 29.0, 10);
  // Cover in the yards and flanks.
  b.car(px(6.0), G, pz(22.0), M('paint_cyan'), true);
  place(11.0, 17.5, 1.5, () => {
    b.crate(px(10.0), G, pz(17.0), 10);
    b.crate(px(10.0) + 11, G, pz(17.0), 10);
    b.crate(px(10.0) + 5, G + 10, pz(17.0), 10);
  });
  place(26.0, 8.8, 1.2, () => planter(b, px(24.5), pz(8.4), 30, 8));
  place(22.0, 25.4, 1.2, () => planter(b, px(20.5), pz(25.0), 22, 8, [M('paint_pink'), M('paint_lime'), M('paint_yellow')]));
  place(3.0, 21.0, 1.0, () => b.bush(px(2.8), G, pz(21.0), 5, M('leaves_2')));
  place(25.2, 30.2, 1.5, () => b.bush(px(25.2), G, pz(30.2), 6, M('leaves_2')));
  place(32.0, 31.0, 1.5, () => b.leafyTree(px(32.0), G, pz(31.0), 40, 13, M('leaves_4')));
  place(16.0, 18.0, 1.5, () => b.leafyTree(px(16.5), G, pz(17.6), 46, 15, M('leaves_2')));

  // The street ------------------------------------------------------------------------------------------------------------------
  b.car(px(41.0), G, pz(8.6), M('paint_lime'), false);
  b.car(px(41.0), G, pz(31.0), M('paint_pink'), false);
  bus(b, px(41.2), pz(25.2), 100, M('paint_yellow'), false);
  mailbox(b, px(38.9), pz(15.0), M('paint_red'));
  mailbox(b, px(38.9), pz(24.0), M('paint_blue'));
  bin(b, px(38.8), pz(10.0), M('paint_green'));
  bin(b, px(38.8), pz(33.0), M('paint_teal'));
  lampPost(b, px(39.6), pz(19.0), M('neon_yellow'));
  lampPost(b, px(39.6), pz(12.0), M('neon_pink'));
  lampPost(b, px(39.6), pz(32.0), M('neon_cyan'));
  place(44.0, 5.6, 1.2, () => b.bench(px(43.0), G, pz(5.4), true));
  place(45.0, 7.2, 1.2, () => planter(b, px(43.4), pz(7.0), 40, 6, [M('paint_pink'), M('paint_yellow'), M('paint_lime')]));
  place(45.0, 36.0, 1.2, () => planter(b, px(43.4), pz(37.0), 40, 6, [M('paint_orange'), M('paint_magenta'), M('paint_yellow')]));
  hedge(b, 38.6, 11.0, 38.6, 13.8, 10, 4);
  hedge(b, 38.6, 17.2, 38.6, 20.0, 10, 4);

  // Mirror the west half to the east; then everything on the centre line -----------------------------------------------------------------
  b.symmetrize(SYM);
  {
    // The roundabout: a raised island with a tree, a ring of flowers and a candy-striped flag.
    const cx = SX / 2;
    const cz = pz(19.6);
    b.cylinder(cx, cz, G - 1, G + 2, 30, M('stone_1'));
    b.cylinder(cx, cz, G + 1, G + 3, 27, LAWN);
    for (let a = 0; a < 16; a++) {
      const ax = Math.round(cx + dcos((a * Math.PI) / 8) * 22);
      const az = Math.round(cz + dsin((a * Math.PI) / 8) * 22);
      b.box(ax - 1, G + 3, az - 1, ax + 1, G + 6, az + 1, PETAL[a % PETAL.length]);
    }
    b.leafyTree(cx, G + 3, cz, 62, 20, M('leaves_2'));
    b.box(cx + 12, G + 3, cz + 6, cx + 13, G + 76, cz + 7, M('chrome'));
    b.box(cx + 13, G + 62, cz + 6, cx + 25, G + 74, cz + 7, stripes(M('paint_pink'), M('paint_white'), 3, 'y'));
  }
  {
    // Water tower behind the north hedge: a landmark that rises above the fence.
    const cx = SX / 2;
    const cz = OZ - 24;
    for (const [dx, dz] of [[-9, -9], [9, -9], [-9, 9], [9, 9]]) b.line(cx + dx, G, cz + dz, cx + (dx > 0 ? 4 : -4), G + 70, cz + (dz > 0 ? 4 : -4), M('paint_white'), 2);
    b.cylinder(cx, cz, G + 68, G + 70, 20, M('paint_white'));
    b.cylinder(cx, cz, G + 70, G + 100, 18, stripes(M('paint_pink'), M('paint_white'), 5, 'y'));
    b.cone(cx, G + 100, cz, 19, 18, M('paint_cyan'), 2);
  }

  // Metadata ---------------------------------------------------------------------------------------------------------------------------------
  const gy = G / 10;
  const meta = emptyMeta(gy, SX / 20, SZ / 20);
  const mirror = (x: number, z: number): [number, number] => symPoint(SYM, SX / 10, SZ / 10, x, z);
  const midZ = wz(19.6);
  // Team spawns: two rows in the west yard (and the mirror image in the east yard), facing the house.
  const team0: [number, number][] = [[4.6, 17.0], [6.4, 17.0], [8.2, 17.0], [4.0, 20.4], [5.8, 20.4], [7.6, 20.4], [9.2, 18.7], [3.6, 15.4]];
  for (const [x, z] of team0) {
    const yaw = yawToward(wx(x), wz(z), SX / 20, midZ);
    meta.spawns.push({ team: 0, x: wx(x), y: gy, z: wz(z), yaw });
    const [mx, mz] = mirror(wx(x), wz(z));
    meta.spawns.push({ team: 1, x: mx, y: gy, z: mz, yaw: symYaw(SYM, yaw) });
  }
  // Free-for-all spawns: yards, flanks, the street and the houses' rooms (both sides).
  const ffa: [number, number][] = [
    [19.5, 17.4], [20.5, 8.5], [27.5, 7.6], [19.5, 24.0], [29.0, 30.6], [43.0, 6.5], [45.5, 14.5], [45.5, 32.0], [45.0, 26.5],
    [32.5, 14.8], [36.2, 19.5], [34.2, 16.4],
  ];
  for (const [x, z] of ffa) {
    const yaw = yawToward(wx(x), wz(z), SX / 20, midZ);
    meta.spawns.push({ team: -1, x: wx(x), y: gy, z: wz(z), yaw });
    const [mx, mz] = mirror(wx(x), wz(z));
    meta.spawns.push({ team: -1, x: mx, y: gy, z: mz, yaw: symYaw(SYM, yaw) });
  }
  meta.points.push({ id: 'A', name: 'West Yard', x: wx(14.0), y: gy, z: wz(18.0), r: 3.2 });
  meta.points.push({ id: 'B', name: 'Roundabout', x: SX / 20, y: gy, z: midZ + 5.2, r: 3.0 });
  meta.points.push({ id: 'C', name: 'East Yard', x: mirror(wx(14.0), 0)[0], y: gy, z: wz(18.0), r: 3.2 });
  // Flags stand 7 m from the spawn rows: an enemy at the flag must not be able to shoot respawning players.
  meta.flags.push({ team: 0, x: wx(16.5), y: gy, z: wz(19.4) });
  meta.flags.push({ team: 1, x: mirror(wx(16.5), 0)[0], y: gy, z: wz(19.4) });
  // Pickups on open ground; standAt() puts each on whatever surface is there.
  const picks: [string, number, number][] = [
    ['health', 45.5, 11.0], ['health', 46.0, 28.0], ['health', 20.0, 9.0], ['health', 19.0, 23.6], ['armor', 13.0, 16.4], ['armor', 36.5, 9.4], ['health', 31.5, 29.6],
  ];
  for (const [kind, x, z] of picks) {
    meta.pickups.push({ kind: kind as 'health' | 'armor', x: wx(x), y: b.standAt(wx(x), wz(z), 4.5), z: wz(z) });
    const [mx, mz] = mirror(wx(x), wz(z));
    meta.pickups.push({ kind: kind as 'health' | 'armor', x: mx, y: b.standAt(mx, mz, 4.5), z: mz });
  }

  // Keep the voxels around every spawn clear and make sure there is floor under it.
  for (const s of meta.spawns) {
    const x = Math.floor(s.x * 10);
    const z = Math.floor(s.z * 10);
    const y = Math.round(s.y * 10);
    b.clear(x - 3, y, z - 3, x + 4, y + 18, z + 4);
    if (b.w.get(x, y - 1, z) === AIR) b.set(x, y - 1, z, M('stone_1'));
  }
  return meta;
}

export const pastelCourt: MapDef = {
  id: 'pastel_court',
  name: 'Pastel Court',
  description: 'A toy-bright cul-de-sac: a pastel house and back yard at each end, one open street between them.',
  size: [SX, SY, SZ],
  modes: ['tdm', 'ffa', 'dom', 'ctf', 'sandbox', 'practice'],
  players: [4, 12],
  seed: 5101,
  version: 1,
  theme: {
    skyTop: 0x6ea8f0,
    skyHorizon: 0xfbe6ee,
    fog: 0xf6e4ec,
    fogNear: 50,
    fogFar: 170,
    sun: [0.35, 0.8, 0.5],
    sunColor: 0xfff4e6,
    sunIntensity: 1.3,
    ambient: 0xf8f0ff,
    ambientIntensity: 1.1,
    outside: 0x8ac870,
    music: 'town',
  },
  build,
};
